import { Badge, Box, Grid, Typography } from '@mui/material'
import { useBreadcrumbsData } from 'Common/Breadcrumbs/Breadcrumbs.data'
import { Page } from 'Common/Page/Page'
import { PageHeader, Button } from 'Common/Supplyvue-ui'
import { useParams } from 'react-router-dom'
import { DashboardCardView } from 'Features/NewDashboard/DashboardCard/DashboardCard.view'
import { VolumeChartView } from './VolumeChart.view'
import { CapacityView } from './Capacity.view'
import {
  SelectedWorkcentreType,
  SourceSiteDashboardPageURLParams,
} from './SourceSiteDashboard.types'
import { StockProjectionView } from './StockProjection.view'
import { StockBuildView } from './StockBuild.view'
import { useEffect, useRef, useState } from 'react'
import { useWorkcentreList } from 'Features/WorkCentreSummary/WorkCentreSummary.data'
import { WorkCentreSummaryTableData } from 'Features/WorkCentreSummary/WorkCentreSummary.types'
import FactoryIcon from '@mui/icons-material/Factory'
import { useSourceSites } from 'Features/OperationalPlanner/OperationalPlan.data'
import {
  SourceSiteDetails,
  WorkcentreDetails,
} from 'Features/OperationalPlanner/WorkcentreModal/WorkcentreModal.types'
import { WorkcentreModal } from 'Features/OperationalPlanner/WorkcentreModal/WorkcentreModal.view'
import { useDashboardState } from 'Features/NewDashboard/Dashboard.state'
import { DASHBOARD_MODE_TEXT } from 'Features/NewDashboard/Dashboard.constants'
import { SplitButton } from 'Features/NewDashboard/DashboardToggle/SplitButton'
import { StockProjectionExceptionPage } from './StockProjections/StockProjectionException.page'

const SOURCE_SITE_DASHBOARD_OPTIONS = {
  VOLUME_PROJECTION: 'Volume projection',
  CAPACITY: 'Capacity projection',
  STOCK_PROJECTION: 'Stock projection exception',
  INVENTORY_PROJECTION: 'Inventory projection',
}

type RightElementButtonProps = {
  onClick: () => void
  selectedWorkcentres: string[]
}
const RightElementButton = ({
  onClick,
  selectedWorkcentres,
}: RightElementButtonProps) => {
  return (
    <Badge
      badgeContent={selectedWorkcentres?.length ?? 0}
      color="secondary"
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <Button
        onClick={onClick}
        variant="contained"
        startIcon={<FactoryIcon />}
        size="large"
      >
        Select workcentres
      </Button>
    </Badge>
  )
}

type WorkcentresInThePlanType = {
  [key: number]: {
    workCentreCode: string
    siteKey: number
  }
}
export const SourceSiteDashboardPage = () => {
  const { planId } = useParams<SourceSiteDashboardPageURLParams>()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedWorkcentres, setSelectedWorkcentres] = useState<string[]>([])
  const [selectedSourceSites, setSelectedSourceSites] = useState<
    SourceSiteDetails[]
  >([])
  const allWorkcentres = useRef<
    SelectedWorkcentreType<'workCentreCode', 'workCentreKey'>[]
  >([])
  const [dashboardDisplayState, updateDashboardState] = useDashboardState()

  useBreadcrumbsData({
    plan_id: planId,
  })
  const tenant = localStorage.getItem('tenantId') ?? ''
  const { data: workcentreList } = useWorkcentreList(planId)

  const { data: sourceSites } = useSourceSites(tenant)

  useEffect(() => {
    // We have complete workcentre list and source sites list from different apis.
    // Now select only those workcentres which are present in the plan.
    // For each source site, filter out the workcentres which are present in the plan.

    if (sourceSites?.length > 0 && workcentreList?.data) {
      const workcentresInThePlan: WorkcentresInThePlanType = {}
      workcentreList.data.forEach((wc: WorkCentreSummaryTableData) => {
        workcentresInThePlan[wc.workCentreKey] = {
          workCentreCode: wc.workCentreCode,
          siteKey: parseInt(`${wc.baseSourceSiteKey}`),
        }
      })

      let filteredSourceSites: SourceSiteDetails[] = []

      sourceSites.forEach((site: SourceSiteDetails) => {
        const workcentres: WorkcentreDetails[] = []
        site.siteWorkcentres.forEach((wc: WorkcentreDetails) => {
          const wcKey = parseInt(`${wc.key}`)
          if (workcentresInThePlan[wcKey]) {
            workcentres.push({ ...wc, isSelected: true })
          }
        })
        if (workcentres.length > 0) {
          filteredSourceSites.push({
            ...site,
            isSelected: true,
            siteWorkcentres: workcentres,
          })
        }
      })
      setSelectedSourceSites(filteredSourceSites)
    }
  }, [sourceSites, workcentreList])

  useEffect(() => {
    if (workcentreList?.data) {
      const list = workcentreList.data.map((wc: WorkCentreSummaryTableData) => {
        return { key: wc.workCentreCode, value: wc.workCentreKey }
      })

      setSelectedWorkcentres(list.map((wc: Record<string, string>) => wc.value))
      allWorkcentres.current = [...list]
    }
  }, [workcentreList])

  const handleSelectedOption = (value: string) => {
    if (value === DASHBOARD_MODE_TEXT) {
      updateDashboardState({
        type: 'DASHBOARD_MODE',
        value: DASHBOARD_MODE_TEXT,
      })
    } else {
      updateDashboardState({
        type: 'SUB_MODE',
        value: value,
      })
    }
  }

  const getSelectedSites = (sourceSiteData: SourceSiteDetails[]) => {
    let selectedWorkcenterKeys: string[] = []

    sourceSiteData.forEach((sourceSite) => {
      if (sourceSite.isSelected) {
        const workcentres = sourceSite.siteWorkcentres
          .filter((workcentre) => workcentre.isSelected)
          .map((wc) => wc.key)
        selectedWorkcenterKeys.push(...workcentres)
      }
    })
    setSelectedWorkcentres(selectedWorkcenterKeys)
    setSelectedSourceSites(sourceSiteData)
    setIsModalOpen(false)
  }

  const title = dashboardDisplayState.subMode
    ? dashboardDisplayState.title
    : 'Workcentres'

  return (
    <Page>
      <Box display="flex" flexDirection="column" flex={1}>
        <PageHeader
          title={title}
          rightElement={
            <RightElementButton
              onClick={() => setIsModalOpen(true)}
              selectedWorkcentres={selectedWorkcentres}
            />
          }
        />
        <Box display="flex" height="50px" alignItems="center">
          <Box display="flex" alignItems="center">
            <Box>
              <Typography>Please select mode</Typography>
            </Box>
            <Box marginLeft={2}>
              <SplitButton
                setSelectedButtonIndex={() => {}}
                enabled={true}
                options={Object.values(SOURCE_SITE_DASHBOARD_OPTIONS)}
                disabledOptions={[
                  SOURCE_SITE_DASHBOARD_OPTIONS.VOLUME_PROJECTION,
                  SOURCE_SITE_DASHBOARD_OPTIONS.CAPACITY,
                  SOURCE_SITE_DASHBOARD_OPTIONS.INVENTORY_PROJECTION,
                ]}
                handleSelectedOption={handleSelectedOption}
                handleToggleButton={() => {}}
              />
            </Box>
          </Box>
        </Box>

        <Box>
          {dashboardDisplayState.subMode ===
          SOURCE_SITE_DASHBOARD_OPTIONS.STOCK_PROJECTION ? (
            <Box>
              <StockProjectionExceptionPage workcentresList={selectedWorkcentres} />
            </Box>
          ) : dashboardDisplayState.subMode === '' ? (
            <Grid container spacing={2}>
              <Grid size={6}>
                <DashboardCardView
                  fullHeight
                  title="Volume projection"
                  onClick={() => {
                    updateDashboardState({
                      type: 'SUB_MODE',
                      value: SOURCE_SITE_DASHBOARD_OPTIONS.VOLUME_PROJECTION,
                    })
                  }}
                >
                  <VolumeChartView workcentresList={selectedWorkcentres} />
                </DashboardCardView>
              </Grid>
              <Grid size={6}>
                <DashboardCardView
                  fullHeight
                  title="Capacity projection"
                  onClick={() => {
                    updateDashboardState({
                      type: 'SUB_MODE',
                      value: SOURCE_SITE_DASHBOARD_OPTIONS.CAPACITY,
                    })
                  }}
                >
                  <CapacityView workcentresList={selectedWorkcentres} />
                </DashboardCardView>
              </Grid>
              <Grid size={6}>
                <DashboardCardView
                  fullHeight
                  title="Stock projection exception"
                  onClick={() => {
                    updateDashboardState({
                      type: 'SUB_MODE',
                      value: SOURCE_SITE_DASHBOARD_OPTIONS.STOCK_PROJECTION,
                    })
                  }}
                >
                  <StockProjectionView workcentresList={selectedWorkcentres} />
                </DashboardCardView>
              </Grid>
              <Grid size={6}>
                <DashboardCardView
                  fullHeight
                  title="Inventory projection"
                  onClick={() => {
                    updateDashboardState({
                      type: 'SUB_MODE',
                      value:
                        SOURCE_SITE_DASHBOARD_OPTIONS.INVENTORY_PROJECTION,
                    })
                  }}
                >
                  <StockBuildView workcentresList={selectedWorkcentres} />
                </DashboardCardView>
              </Grid>
            </Grid>
          ) : null}
        </Box>
      </Box>
      {isModalOpen && (
        <WorkcentreModal
          isModalOpen={isModalOpen}
          handleModalClose={() => setIsModalOpen(false)}
          handleOkbuttonClick={getSelectedSites}
          isUpdating={false}
          readOnly={false}
          sourceSiteData={selectedSourceSites ?? []}
          templateSourceSiteDetails={[]}
        />
      )}
    </Page>
  )
}
